import {
  SupportedDownloadFileType,
  type IDownloadedFileResponseModel,
  type IDownloadedFileViewModel,
  type IExtractedResultModel,
  type IPresignedUrlResponseModel,
  type IProcessInfoModel,
  type IProcessInfoViewModel,
  type IProcessedResultsFilterViewModel,
  type IProcessListResponse,
  type IProcessListViewModel,
  type IProcessDetailResponse,
  type IProcessDetailViewModel,
  type UserInstructionResponse,
  type UserInstructionViewModel,
  type IMappingDispatchResponse,
} from "@models/index";
import type {
  BatchProcessSummaryResponse,
  BatchProcessSummaryViewModel,
} from "@models/monitoring";
import { buildBatchProcessSummaryViewModel } from "@utils/builders/monitoring";
import {
  buildProcessedInfoViewModel,
  buildUserInstructionViewModel,
  buildProcessListViewModel,
  buildProcessDetailViewModel,
  HttpClient,
} from "@utils/index";

const BASE_URL = import.meta.env.VITE_EXTRACTION_API_BASE_URL;

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_EXTRACTION_API_BASE_URL. " +
      "Please check your .env file and ensure the Intelligence Engine API URL is set.",
  );
}

class IntelligenceEngineApi {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient(`${BASE_URL}`);
  }

  public async requestPresignedUrl(
    fileName: string,
    doctypeCode: string,
    pageNumbers?: string,
    instructions?: string,
    mappingEnabled?: boolean,
  ): Promise<IPresignedUrlResponseModel> {
    const endpoint = `/processes`;
    return this.httpClient.makeAuthenticatedRequest<IPresignedUrlResponseModel>(
      endpoint,
      {
        method: "POST",
        body: JSON.stringify({
          file_name: fileName,
          doctype_code: doctypeCode,
          page_numbers: pageNumbers || undefined,
          instructions: instructions || undefined,
          mapping_enabled: mappingEnabled ?? false,
        }),
      },
    );
  }

  public async uploadFileToS3(
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("Content-Type", contentType);

      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(
            new Error(`File upload failed: ${xhr.status} ${xhr.statusText}`),
          );
        }
      };
      xhr.onerror = () =>
        reject(new Error("File upload failed: network error"));
      xhr.onabort = () => reject(new Error("File upload aborted"));

      xhr.send(file);
    });
  }

  public async getExtractedProcess(
    processId: string,
    itemId: string,
  ): Promise<IExtractedResultModel> {
    const endpoint = `/processes/${processId}/items/${itemId}/result`;
    return this.httpClient.makeAuthenticatedRequest<IExtractedResultModel>(
      endpoint,
      {
        method: "GET",
      },
    );
  }

  public async searchExtractionProcesses(
    doctypeCode: string,
    filter: IProcessedResultsFilterViewModel,
    cursor?: string | null,
    limit?: number,
  ): Promise<IProcessListViewModel> {
    const queryParams = new URLSearchParams();
    const resolvedLimit = limit ?? filter.limit ?? 10;

    queryParams.append("doctype_code", doctypeCode);
    queryParams.append("limit", String(resolvedLimit));

    if (filter.startDate) {
      queryParams.append("start_date", filter.startDate);
    }
    if (filter.endDate) {
      queryParams.append("end_date", filter.endDate);
    }
    if (filter.fileName) {
      queryParams.append("filename", filter.fileName);
    }
    if (filter.userId) {
      queryParams.append("user_id", filter.userId);
    }
    if (filter.processId) {
      queryParams.append("process_id", filter.processId);
    }
    if (cursor) {
      queryParams.append("cursor", cursor);
    }

    const endpoint = `/processes?${queryParams.toString()}`;

    const response =
      await this.httpClient.makeAuthenticatedRequest<IProcessListResponse>(
        endpoint,
        {
          method: "GET",
        },
      );
    return buildProcessListViewModel(response);
  }

  public async getExtractionProcessesInfo(
    processId: string,
  ): Promise<IProcessInfoViewModel> {
    const endpoint = `/processes/${processId}/general-info`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<IProcessInfoModel>(
        endpoint,
        {
          method: "GET",
        },
      );
    return buildProcessedInfoViewModel(response);
  }

  public async getProcessDetailWithItems(
    processId: string,
  ): Promise<IProcessDetailViewModel> {
    const endpoint = `/processes/${processId}`;
    const response =
      await this.httpClient.makeAuthenticatedRequest<IProcessDetailResponse>(
        endpoint,
        {
          method: "GET",
        },
      );
    return buildProcessDetailViewModel(response);
  }

  public async getRawResult(processId: string): Promise<{
    status: string;
    result: Record<string, unknown>;
    error_message: string | null;
  }> {
    const endpoint = `/processes/${processId}/raw-result`;
    return this.httpClient.makeAuthenticatedRequest(endpoint, {
      method: "GET",
    });
  }

  public async downloadResult(
    processId: string,
    itemId: string,
    fileType: SupportedDownloadFileType = SupportedDownloadFileType.Excel,
  ): Promise<IDownloadedFileViewModel> {
    const endpoint = `/processes/${processId}/items/${itemId}/result-files?file_type=${fileType}`;

    const downloadInfo =
      await this.httpClient.makeAuthenticatedRequest<IDownloadedFileResponseModel>(
        endpoint,
        {
          method: "POST",
        },
      );

    const fileResponse = await fetch(downloadInfo.s3_key);

    if (!fileResponse.ok) {
      throw new Error(`Failed to download file: ${fileResponse.statusText}`);
    }

    const blob = await fileResponse.blob();

    return {
      fileName: downloadInfo.file_name,
      file: blob,
    };
  }

  public async downloadBatchResults(
    processId: string,
  ): Promise<IDownloadedFileViewModel> {
    const endpoint = `/processes/${processId}/result-files/archive`;

    const downloadInfo =
      await this.httpClient.makeAuthenticatedRequest<IDownloadedFileResponseModel>(
        endpoint,
        {
          method: "POST",
        },
      );

    const fileResponse = await fetch(downloadInfo.s3_key);

    if (!fileResponse.ok) {
      throw new Error(`Failed to download file: ${fileResponse.statusText}`);
    }

    const blob = await fileResponse.blob();

    return {
      fileName: downloadInfo.file_name,
      file: blob,
    };
  }

  public async downloadInputFile(
    processId: string,
  ): Promise<IDownloadedFileViewModel> {
    const endpoint = `/processes/${processId}/input-file`;

    const downloadInfo =
      await this.httpClient.makeAuthenticatedRequest<IDownloadedFileResponseModel>(
        endpoint,
        {
          method: "POST",
        },
      );

    const fileResponse = await fetch(downloadInfo.s3_key);

    if (!fileResponse.ok) {
      throw new Error(`Failed to download file: ${fileResponse.statusText}`);
    }

    const blob = await fileResponse.blob();

    return {
      fileName: downloadInfo.file_name,
      file: blob,
    };
  }

  /**
   * Trigger mapping on-demand for a completed process item whose run did not
   * opt in to mapping at upload time. The mapping job is created asynchronously
   * downstream; callers then poll/subscribe by (processId, itemId) for status.
   * Idempotent server-side (an already-dispatched item is a no-op).
   */
  public async dispatchMapping(
    processId: string,
    itemId: string,
  ): Promise<IMappingDispatchResponse> {
    const endpoint = `/processes/${processId}/items/${itemId}/dispatch-mapping`;
    return this.httpClient.makeAuthenticatedRequest<IMappingDispatchResponse>(
      endpoint,
      { method: "POST" },
    );
  }

  public downloadFile(downloaded: IDownloadedFileViewModel): void {
    const url = window.URL.createObjectURL(downloaded.file);
    const link = document.createElement("a");
    link.href = url;
    link.download = downloaded.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  public async getProcessesBatch(
    processIds: string[],
  ): Promise<BatchProcessSummaryViewModel[]> {
    const endpoint = `/processes/batch`;

    const response = await this.httpClient.makeAuthenticatedRequest<
      BatchProcessSummaryResponse[]
    >(endpoint, {
      method: "POST",
      body: JSON.stringify({
        process_ids: processIds,
      }),
    });

    return response.map(buildBatchProcessSummaryViewModel);
  }

  public async listUserInstructions(
    doctypeCode: string,
  ): Promise<UserInstructionViewModel[]> {
    const params = new URLSearchParams({ doctype_code: doctypeCode });
    const response = await this.httpClient.makeAuthenticatedRequest<
      UserInstructionResponse[]
    >(`/instructions?${params}`, { method: "GET" });
    return response.map(buildUserInstructionViewModel);
  }

  public async saveUserInstruction(payload: {
    name: string;
    instruction: string;
    doctype_code: string;
  }): Promise<UserInstructionViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<UserInstructionResponse>(
        `/instructions`,
        { method: "POST", body: JSON.stringify(payload) },
      );
    return buildUserInstructionViewModel(response);
  }

  public async deleteUserInstruction(
    doctypeCode: string,
    instructionId: string,
  ): Promise<void> {
    const params = new URLSearchParams({ doctype_code: doctypeCode });
    await this.httpClient.makeAuthenticatedRequest<void>(
      `/instructions/${instructionId}?${params}`,
      { method: "DELETE" },
    );
  }

  public async updateUserInstruction(
    doctypeCode: string,
    instructionId: string,
    payload: { name: string; instruction: string },
  ): Promise<UserInstructionViewModel> {
    const params = new URLSearchParams({ doctype_code: doctypeCode });
    const response =
      await this.httpClient.makeAuthenticatedRequest<UserInstructionResponse>(
        `/instructions/${instructionId}?${params}`,
        { method: "PUT", body: JSON.stringify(payload) },
      );
    return buildUserInstructionViewModel(response);
  }

  public async deleteProcess(processId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest<void>(
      `/processes/${processId}`,
      { method: "DELETE" },
    );
  }

  public async deleteProcessItem(
    processId: string,
    itemId: string,
  ): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest<void>(
      `/processes/${processId}/items/${itemId}`,
      { method: "DELETE" },
    );
  }
}

export { IntelligenceEngineApi };

export const intelligenceEngineApi = new IntelligenceEngineApi();
